import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Maximize, Pause, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";

const COVER = "/media/video-cover.jpg";

const fmt = (s) => {
  if (!s || Number.isNaN(s)) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const VideoPlayer = ({ src }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);

  // Avvio affidabile al primo tocco: play() dentro il gesto dell'utente,
  // con gestione della Promise e un solo retry se il caricamento interrompe la richiesta.
  const attemptPlay = useCallback(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    setFailed(false);
    setLoading(true);
    let p;
    try {
      p = v.play();
    } catch {
      p = null;
    }
    if (!p || typeof p.then !== "function") {
      setLoading(false);
      return;
    }
    p.then(() => setLoading(false)).catch((err) => {
      if (err && err.name === "AbortError") {
        // Il caricamento della sorgente ha interrotto play(): riprova appena il video è pronto.
        const retry = () => {
          v.play()
            .then(() => setLoading(false))
            .catch(() => {
              setLoading(false);
              setFailed(true);
            });
        };
        if (v.readyState >= 2) retry();
        else v.addEventListener("canplay", retry, { once: true });
      } else {
        setLoading(false);
        setFailed(true);
      }
    });
  }, [src]);

  // Avvio richiesto dal pulsante "GUARDA IL VIDEO" dell'hero (stesso gesto utente, senza attendere lo scroll)
  useEffect(() => {
    window.addEventListener("duoclair:play-video", attemptPlay);
    return () => window.removeEventListener("duoclair:play-video", attemptPlay);
  }, [attemptPlay]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) attemptPlay();
    else v.pause();
  };

  const seek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    v.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  const progress = duration ? (time / duration) * 100 : 0;

  return (
    <div className="relative bg-black" data-testid="video-player">
      <div className="absolute inset-x-0 top-0 h-px gold-hairline z-20" />
      <video
        ref={videoRef}
        src={src || undefined}
        poster={COVER}
        muted={muted}
        playsInline
        preload="metadata"
        className="w-full aspect-video object-cover cursor-pointer"
        onClick={toggle}
        onPlay={() => {
          setPlaying(true);
          setStarted(true);
          setLoading(false);
          setFailed(false);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onPlaying={() => setLoading(false)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onError={() => {
          setLoading(false);
          setFailed(true);
        }}
        data-testid="video-element"
      />

      {/* Copertina + Play: visibili solo prima del primo avvio */}
      {!started && !failed && (
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          onClick={attemptPlay}
          data-testid="video-cover"
        >
          <img
            src={COVER}
            alt="DuoClair — Cambia il modo in cui ti vedono"
            className="w-full h-full object-cover select-none"
            draggable="false"
          />
          {loading ? (
            <span className="absolute left-1/2 top-[74%] -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-gold/50 bg-black/70">
              <Loader2 className="w-6 h-6 sm:w-7 sm:h-7 text-gold animate-spin" />
            </span>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                attemptPlay();
              }}
              data-testid="video-play-button"
              aria-label="Riproduci la presentazione DuoClair"
              className="absolute left-1/2 top-[74%] -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full border border-gold/60 bg-black/70 backdrop-blur-sm transition-transform duration-300 hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
            >
              <Play className="w-6 h-6 sm:w-7 sm:h-7 text-gold fill-gold ml-0.5" />
            </button>
          )}
        </div>
      )}

      {/* Errore: messaggio chiaro + comando per riprovare */}
      {failed && (
        <div className="absolute inset-0 z-10" data-testid="video-fallback-message">
          <img src={COVER} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/60 px-6">
            <p className="text-center text-sm sm:text-base text-slate-200 leading-relaxed">
              Il video non si è avviato. Controlla la connessione e riprova.
            </p>
            <button
              onClick={attemptPlay}
              data-testid="video-retry-button"
              className="inline-flex items-center gap-2 rounded-full border border-gold/50 bg-black/60 px-6 py-3 text-sm font-semibold text-gold-light hover:bg-gold/10 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Riprova
            </button>
          </div>
        </div>
      )}

      {/* Controlli: disponibili dal primo avvio in poi */}
      {started && !failed && (
        <div className="absolute inset-x-0 bottom-0 z-20 px-4 pb-3 pt-10 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
          <div
            className="group/bar relative h-1.5 w-full rounded-full bg-white/15 cursor-pointer mb-3"
            onClick={seek}
            data-testid="video-progress-bar"
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-gold-dark via-gold to-gold-light"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-gold-light shadow-[0_0_12px_rgba(212,160,60,0.8)] opacity-0 group-hover/bar:opacity-100 transition-opacity"
              style={{ left: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={toggle} data-testid="video-toggle-button" aria-label={playing ? "Pausa" : "Play"} className="text-slate-200 hover:text-gold transition-colors">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 fill-current" />}
              </button>
              <button
                onClick={() => {
                  setMuted(!muted);
                  if (videoRef.current) videoRef.current.muted = !muted;
                }}
                data-testid="video-mute-button"
                aria-label={muted ? "Attiva audio" : "Silenzia"}
                className="text-slate-200 hover:text-gold transition-colors"
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <span className="font-mono text-[11px] text-zinc-400 tabular-nums" data-testid="video-time">
                {fmt(time)} <span className="text-zinc-600">/ {fmt(duration) || "1:00"}</span>
              </span>
            </div>
            <button
              onClick={() => videoRef.current?.requestFullscreen?.()}
              data-testid="video-fullscreen-button"
              aria-label="Schermo intero"
              className="text-slate-200 hover:text-gold transition-colors"
            >
              <Maximize className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
